/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  createStyles,
  Card,
  Group,
  Text,
  Select,
  TextInput,
  Table,
  Avatar,
  ActionIcon,
  Pagination,
  Badge,
  Center,
  Collapse,
  Grid,
  Modal,
  Image,
  Box,
  Tooltip,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  IconSearch,
  IconChevronDown,
  IconPhoto,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import EditAttendanceModal from "features/attendance-admin/components/EditAttendanceModal";
import DeleteAttendanceModal from "features/attendance-admin/components/DeleteAttendanceModal";
import useGetAttendanceReport, {
  AttendanceRecord,
} from "api/attendanceReport/getAttendanceReport";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetTotalHour } from "api/attendance/getUserWorkingHours";
import { formatDateToDDMMYYYY, formatToLocalTime } from "utils/dateFormate";
import { useUser } from "hooks/useUser";
import { useHasPermission } from "api/permission/useHasPermission";

const useStyles = createStyles((theme) => ({
  page: { padding: theme.spacing.xl, width: "100%" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing.md,
  },
  title: { fontSize: 18, fontWeight: 700 },
  controls: { display: "flex", gap: theme.spacing.sm, alignItems: "center" },
  field: {
    width: "100%",
  },
  tableWrap: { marginTop: theme.spacing.sm, minHeight: 300 },
  employeeCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  empMeta: { lineHeight: 1 },
  empName: { fontWeight: 600, fontSize: 14 },
  smallMuted: { fontSize: 12, color: theme.colors.gray[6] },
  nestedRow: {
    backgroundColor: theme.colors.gray[0],
    padding: theme.spacing.sm,
  },
  imageIcon: {
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
      color: theme.colors.blue[6],
    },
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: theme.spacing.md,
  },
  imageContainer: {
    borderRadius: theme.radius.md,
    overflow: "hidden",
    border: `1px solid ${theme.colors.gray[3]}`,
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },
  imageCountBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 2,
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 600,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "white",
  },
}));

const departmentRules: Record<
  string,
  { allowedStart: string; allowedHours: number }
> = {
  Sales: { allowedStart: "09:00", allowedHours: 8 },
  HR: { allowedStart: "09:30", allowedHours: 8 },
  Engineering: { allowedStart: "10:00", allowedHours: 9 },
  Finance: { allowedStart: "09:00", allowedHours: 8 },
};

const formatDate = (iso?: string | Date | null) => {
  if (!iso) return "-";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleDateString();
  } catch (e) {
    return String(iso);
  }
};
const formatTime = (iso?: string | null) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return String(iso);
  }
};
const diffMinutes = (inIso?: string | null, outIso?: string | null) => {
  if (!inIso || !outIso) return 0;
  const a = new Date(inIso).getTime();
  const b = new Date(outIso).getTime();
  if (isNaN(a) || isNaN(b) || b <= a) return 0;
  return Math.round((b - a) / (1000 * 60));
};
const minutesToHours = (m: number) => (m / 60).toFixed(2);

function isoDateString(d: Date | null) {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
interface AttendanceReportTable {
  onFilterChange?: (filters: any) => void;
  dateRange: [Date | null, Date | null];
  onDateRangeChange: (range: [Date | null, Date | null]) => void;
}
export default function AttendanceReportTable({
  onFilterChange,
  dateRange,
  onDateRangeChange,
}: AttendanceReportTable) {
  const { classes } = useStyles();
  const { user } = useUser();

  // ✅ STEP 1: Check permission
  const { has: hasAttendanceReportPermission, isLoading: permissionLoading } =
    useHasPermission("reports.attendanceReports");

  const { data: departmentsData, isLoading: isLoadingDepartments } =
    useGetDepartments();
  const { data: workingHours, isLoading: hoursLoading } = useGetTotalHour();
  const AllowedHours = workingHours?.totalHours ?? 0;
  
  // ✅ STEP 2: Determine role-based restrictions
  const roleNormalized = (user?.role ?? "").toString().toLowerCase();
  const isSuperAdmin = roleNormalized === "superadmin";
  const isAdmin = roleNormalized === "admin";
  const isChief = roleNormalized === "chief";
  const isDirector = roleNormalized === "director";
  const isSupervisor = roleNormalized === "supervisor";
  const isMember = roleNormalized === "member";
  
  // Users who can see all data
  const canSeeAllData = isSuperAdmin || isAdmin || isChief;
  // Only members should have disabled department filter
  const disabledDept = isMember;

  const [department, setDepartment] = useState<string | null>(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // New state for images modal
  const [imagesModalOpened, setImagesModalOpened] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>("");

  // ✅ STEP 4: Use the filtered departments from backend
  // Backend getAllDepartments already filters by role:
  // - Directors: Only departments where they are director
  // - Supervisors: Only departments they belong to (from programs they supervise)
  // - Members: Only departments they belong to
  // - Admin/SuperAdmin/Chief: All departments
  const deptArray: any[] = departmentsData?.data ?? [];

  // ✅ STEP 3: Auto-set department based on role
  useEffect(() => {
    if (!user) return;
    
    if (isMember) {
      // Members can only see themselves - no department filter
      setDepartment(null);
      setPage(1);
    } else if (isDirector) {
      // Directors: Backend already filtered to show only their departments
      // If they have only one department, auto-select it
      // Otherwise, let them choose
      if (deptArray.length === 1) {
        setDepartment(deptArray[0]._id);
        setPage(1);
      } else {
        // Don't auto-set if multiple departments - let them choose
        setPage(1);
      }
    } else if (isSupervisor) {
      // Supervisors: Backend filtered to their departments
      // If they have only one department, auto-select it
      if (deptArray.length === 1) {
        setDepartment(deptArray[0]._id);
        setPage(1);
      } else if (user.department?._id) {
        // Fallback to their own department
        setDepartment(String(user.department._id));
        setPage(1);
      }
    } else if (canSeeAllData) {
      // Admin/SuperAdmin/Chief can select any department
      setDepartment(null);
    } else {
      setDepartment((prev) => prev ?? null);
    }
  }, [user, isMember, isDirector, isSupervisor, canSeeAllData, deptArray]);

  const startDateParam = isoDateString(dateRange[0] ?? null) ?? undefined;
  const endDateParam = isoDateString(dateRange[1] ?? null) ?? undefined;
  const searchParam = employeeSearch?.trim() ? employeeSearch.trim() : undefined;

  const params = useMemo(
    () => ({
      startDate: startDateParam,
      endDate: endDateParam,
      department: department ?? undefined,
      status: statusFilter ?? undefined,
      page,
      limit: rowsPerPage,
      search: searchParam,
    }),
    [
      startDateParam,
      endDateParam,
      department,
      statusFilter,
      page,
      rowsPerPage,
      searchParam,
    ]
  );

  const resp = useGetAttendanceReport(params);

  const records: AttendanceRecord[] =
    resp?.data?.data?.records && Array.isArray(resp.data.data.records)
      ? resp.data.data.records
      : [];

  // Row Edit/Delete state (reuses the attendance-admin edit/delete modals).
  const [editRec, setEditRec] = useState<AttendanceRecord | null>(null);
  const [deleteRecId, setDeleteRecId] = useState<string | null>(null);

  const pagination = resp?.data?.data?.pagination ?? {
    currentPage: page,
    totalPages: 1,
    totalRecords: records.length,
    recordsPerPage: rowsPerPage,
  };

  // ✅ STEP 5: Filter department options - backend already did role-based filtering
  const deptOptions = useMemo(() => {
    if (!Array.isArray(deptArray)) return [];
    
    // Backend already filtered based on role, so just map to options
    const base = deptArray.map((d) => ({
      value: d._id,
      label: d.name,
    }));

    // For members, hide department filter entirely
    if (isMember) {
      return [];
    }

    // For supervisors, if they have a department but it's not in the list, add it
    // (This handles edge cases where supervisor's department might not be in filtered results)
    if (isSupervisor && user?.department?._id) {
      const myId = String(user.department._id);
      const exists = base.some((b) => String(b.value) === myId);
      if (!exists) {
        const myLabel = user.department?.name ?? "My Department";
        base.unshift({ value: myId, label: myLabel });
      }
    }

    return base;
  }, [deptArray, isMember, isSupervisor, user]);

  const computeRow = (row: AttendanceRecord) => {
    const deptName = row.employee?.department?.name ?? "General";
    const deptRule = departmentRules[deptName] || {
      allowedStart: "09:00",
      allowedHours: AllowedHours,
    };
    const totalMinutes = diffMinutes(row.checkIn ?? null, row.checkOut ?? null);
    const totalHours = row?.productionHours;

    let late = row?.isLate;
    let lateMinutes = row.lateMinutes ?? 0;

    let productiveMinutes = 0;
    if (row.status === "Present" || row.status?.toLowerCase() === "late") {
      productiveMinutes = Math.max(0, totalMinutes - lateMinutes);
    }

    let overTime = row.overtimeMinutes;
    let overtimeRate = row.overtimeRate;
    let calculatedOvertime = row.calculatedOvertime;

    let overTimeStatus = "No OverTime";
    if (row.isOvertime && row.isOvertimeApproved) {
      overTimeStatus = "Approved";
    } else if (row.isOvertime && !row.isOvertimeApproved) {
      overTimeStatus = "Pending";
    }

    return {
      totalMinutes,
      totalHours,
      late,
      lateMinutes,
      productiveHours: Number(minutesToHours(productiveMinutes)),
      allowedWorkingHours: deptRule.allowedHours,
      overTime,
      overTimeStatus,
      overtimeRate,
      calculatedOvertime,
    };
  };

  // --- Handle Image Icon Click ---
  const handleImageClick = (images: string[], employeeName: string) => {
    if (images && images.length > 0) {
      setSelectedImages(images);
      setSelectedEmployeeName(employeeName);
      setImagesModalOpened(true);
    }
  };

  // --- Render Image Icon or Dash ---
  const renderImageCell = (images: string[], employeeName: string) => {
    if (!images || images.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          -
        </Text>
      );
    }

    return (
      <Tooltip
        label={`View ${images.length} image${images.length > 1 ? "s" : ""}`}
        position="top"
      >
        <ActionIcon
          className={classes.imageIcon}
          color="blue"
          onClick={() => handleImageClick(images, employeeName)}
        >
          <IconPhoto size={20} />
          {images.length > 1 && (
            <Text
              size="xs"
              fw={700}
              style={{ position: "absolute", top: -8, right: -8 }}
            >
              {images.length}
            </Text>
          )}
        </ActionIcon>
      </Tooltip>
    );
  };

  const rows = records.map((item:any) => {
    const id = `${item.employee?.id ?? item.employee?.name}-${
      item.day ?? Math.random()
    }`;
    const c = computeRow(item);

    const initials =
      (item.employee?.name || "N/A")
        .split(" ")
        .map((n:any) => (n ? n[0] : ""))
        .slice(0, 2)
        .join("")
        .toUpperCase() || "NA";

    return (
      <React.Fragment key={id}>
        <tr>
          <td>
            <div className={classes.employeeCell}>
              <Avatar
                size={36}
                radius={36}
                src={item.employee?.profileImage || undefined}
              >
                {!item.employee?.profileImage ? initials : null}
              </Avatar>
              <div>
                <div className={classes.empName}>
                  {item.employee?.name ?? "-"}
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {item.employee?.position ??
                    item.employee?.department?.name ??
                    "-"}
                </div>
              </div>
            </div>
          </td>

          <td>{formatDateToDDMMYYYY(item.day ? item.day : "")}</td>
          <td>{item.checkIn ? formatToLocalTime(item.checkIn) : "-"}</td>
          <td>{item.checkOut ? formatToLocalTime(item.checkOut) : "-"}</td>
          <td>{c.totalHours} hrs</td>
          <td>
            <Badge
              color={
                (item.status || "").toLowerCase() === "present"
                  ? "green"
                  : (item.status || "").toLowerCase() === "absent"
                  ? "red"
                  : "yellow"
              }
            >
              {item.status ?? "-"}
            </Badge>
          </td>
          <td>{item.notes ?? "-"}</td>
          <td>
            {/* Images Column - Add this */}
            {renderImageCell(item.images || [], item.employee?.name || "")}
          </td>
          <td>{item.notes ?? "-"}</td>
          <td>{c.late ? "Yes" : "No"}</td>
          <td>{c.lateMinutes}</td>
          <td>{`${c.totalHours} / ${c.allowedWorkingHours}`}</td>
          <td>
            {item.recordId &&
            (item.status === "Present" || item.status === "Absent") ? (
              <div style={{ display: "flex", gap: 4 }}>
                <PermissionActionIcon
                  permission="attendance.admin"
                  aria-label="Edit attendance"
                  color="blue"
                  variant="subtle"
                  onClick={() => setEditRec(item)}
                >
                  <IconEdit size={16} />
                </PermissionActionIcon>
                <PermissionActionIcon
                  permission="attendance.admin"
                  aria-label="Delete attendance"
                  color="red"
                  variant="subtle"
                  onClick={() => setDeleteRecId(item.recordId!)}
                >
                  <IconTrash size={16} />
                </PermissionActionIcon>
              </div>
            ) : (
              "-"
            )}
          </td>
          <td>
            <ActionIcon
              onClick={() => setExpanded((s) => ({ ...s, [id]: !s[id] }))}
            >
              <IconChevronDown />
            </ActionIcon>
          </td>
        </tr>

        <tr>
          <td colSpan={14} style={{ padding: 0, borderTop: 0 }}>
            <Collapse in={!!expanded[id]} transitionDuration={200}>
              <div className={classes.nestedRow}>
                {item.status === "Holiday" ? (
                  <div>
                    <Text size="sm">
                      <strong>Holiday Type:</strong>{" "}
                      {(item as any).holidayType ?? "-"}
                    </Text>
                    <Text size="sm">Productive Hours: 0</Text>
                  </div>
                ) : (item as any).leaveCollection ? (
                  <div>
                    <Text size="sm">
                      <strong>Leave Collection:</strong>
                    </Text>
                    <Table striped withBorder>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray((item as any).leaveCollection) &&
                        (item as any).leaveCollection.length > 0 ? (
                          (item as any).leaveCollection.map(
                            (l: any, i: number) => (
                              <tr key={i}>
                                <td>{l.type}</td>
                                <td>{formatDate(l.from)}</td>
                                <td>{formatDate(l.to)}</td>
                                <td>{l.days}</td>
                              </tr>
                            )
                          )
                        ) : (
                          <tr>
                            <td colSpan={4}>No leave details</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                    <Text size="sm">Productive Hours: 0</Text>
                  </div>
                ) : (
                  <div>
                    <Grid gutter="md">
                      <Grid.Col span={2}>
                        <Text size="sm">
                          <strong>Total Hours:</strong> {c.totalHours} hrs
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={2}>
                        <Text size="sm">
                          <strong>Productive Hours:</strong> {c.productiveHours}{" "}
                          hrs
                        </Text>
                      </Grid.Col>

                      <Grid.Col span={1}>
                        <Text size="sm">
                          <strong>Late:</strong> {c.late ? "Yes" : "No"}
                        </Text>
                      </Grid.Col>

                      <Grid.Col span={2}>
                        <Text size="sm">
                          <strong>Late Minutes:</strong> {c.lateMinutes}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={2}>
                        <Text size="sm">
                          <strong>OverTime Minutes:</strong> {c.overTime}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <strong>OverTime Status: </strong>

                        <Badge
                          color={
                            c.overTimeStatus === "Approved"
                              ? "green"
                              : c.overTimeStatus === "Pending"
                              ? "orange"
                              : "gray"
                          }
                          variant="light"
                          size="sm"
                        >
                          {c.overTimeStatus}
                        </Badge>
                      </Grid.Col>
                      <Grid.Col span={2}>
                        <Text size="sm">
                          <strong>OverTime Rate:</strong> {c.overtimeRate}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={2}>
                        <Text size="sm">
                          <strong>Calculated Overtime:</strong>{" "}
                          {c.calculatedOvertime}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Text size="sm">
                          <strong>Allowed Working Hours:</strong>{" "}
                          {c.allowedWorkingHours} hrs
                        </Text>
                      </Grid.Col>
                    </Grid>
                  </div>
                )}
              </div>
            </Collapse>
          </td>
        </tr>
      </React.Fragment>
    );
  });

  const handleRowsPerPageChange = (v: string) => {
    const n = parseInt(v || "10", 10);
    setRowsPerPage(n);
    setPage(1);
  };
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(params);
    }
  }, [params]);

  const isLoading = resp?.isLoading ?? false;
  const isError = resp?.isError ?? false;
  const errorMessage =
    (resp as any)?.error?.response?.data?.message ??
    (resp as any)?.error?.message ??
    "";

  // ✅ STEP 6: Show permission denied message if no access
  if (permissionLoading) {
    return (
      <Card shadow="sm" radius="md" p="md">
        <Center py="xl">
          <Text>Loading...</Text>
        </Center>
      </Card>
    );
  }

  if (!hasAttendanceReportPermission) {
    return (
      <Card shadow="sm" radius="md" p="md">
        <Center py="xl">
          <Text color="red" size="lg" weight={600}>
            You don't have permission to view attendance reports
          </Text>
        </Center>
      </Card>
    );
  }

  return (
    <>
      {editRec && (
        <EditAttendanceModal
          opened={!!editRec}
          onClose={() => {
            setEditRec(null);
            resp.refetch?.();
          }}
          recordId={editRec.recordId!}
          employeeName={editRec.employee?.name || ""}
          day={editRec.day || ""}
          initialCheckIn={editRec.checkIn}
          initialCheckOut={editRec.checkOut}
          initialStatus={editRec.status as "Present" | "Absent"}
          initialIsHalfDay={editRec.isHalfDay}
          initialNotes={editRec.notes ?? ""}
        />
      )}
      <DeleteAttendanceModal
        opened={!!deleteRecId}
        onClose={() => {
          setDeleteRecId(null);
          resp.refetch?.();
        }}
        recordId={deleteRecId || ""}
      />
      <Card shadow="sm" radius="md" p="md">
        <div className={classes.header}>
          <div>
            <Text className={classes.title}>Daily Attendance Report</Text>

            <Text size="xs" color="black">
              Selected filters:{" "}
              {dateRange[0] && dateRange[1]
                ? `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`
                : "None"}
            </Text>
            {/* ✅ STEP 7: Show role-based context messages */}
            {isMember && (
              <Text size="xs" color="dimmed" mt={4}>
                Viewing your own attendance records
              </Text>
            )}
            {isSupervisor && (
              <Text size="xs" color="dimmed" mt={4}>
                Viewing attendance records for members in programs you supervise
              </Text>
            )}
            {isDirector && (
              <Text size="xs" color="dimmed" mt={4}>
                Viewing attendance records for departments you manage
                {deptArray.length > 0 && ` (${deptArray.length} department${deptArray.length > 1 ? 's' : ''})`}
              </Text>
            )}
          </div>

          <div className={classes.controls}>
            <Grid gutter="sm">
              {/* Date Range */}
              <Grid.Col xs={12} sm={6} md={3}>
                <DateRangePicker
                  label="Date Range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={(v) => {
                    onDateRangeChange(v);
                    setPage(1);
                  }}
                  className={classes.field}
                />
              </Grid.Col>

              {/* Department - Hide for members, show filtered list for others */}
              {!isMember && (
                <Grid.Col xs={12} sm={6} md={3}>
                  <Select
                    label="Department"
                    placeholder={
                      isDirector 
                        ? "Select department" 
                        : isSupervisor
                        ? "Select department"
                        : "Select department"
                    }
                    data={deptOptions}
                    value={department}
                    onChange={(v) => {
                      setDepartment(v);
                      setPage(1);
                    }}
                    clearable={canSeeAllData} // Only admin/superadmin/chief can clear
                    nothingFound="No departments found"
                    disabled={isLoadingDepartments}
                    className={classes.field}
                    description={
                      isDirector && deptArray.length > 0
                        ? `You manage ${deptArray.length} department${deptArray.length > 1 ? 's' : ''}`
                        : undefined
                    }
                  />
                </Grid.Col>
              )}

              {/* Status */}
              <Grid.Col xs={12} sm={6} md={3}>
                <Select
                  label="Status"
                  placeholder="Select status"
                  data={["Present", "Absent", "Late", "Holiday", "Leave"].map(
                    (s) => ({
                      value: s,
                      label: s,
                    })
                  )}
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v);
                    setPage(1);
                  }}
                  clearable
                  className={classes.field}
                />
              </Grid.Col>

              {/* Employee Search */}
              <Grid.Col xs={12} sm={6} md={3}>
                <TextInput
                  label="Employee Name"
                  placeholder="Search employee"
                  icon={<IconSearch size={14} />}
                  value={employeeSearch}
                  onChange={(e) => {
                    setEmployeeSearch(e.currentTarget.value);
                    setPage(1);
                  }}
                  className={classes.field}
                />
              </Grid.Col>
            </Grid>
          </div>
        </div>

        <div className={classes.tableWrap}>
          <Table verticalSpacing="sm" highlightOnHover>
            <thead style={{ backgroundColor: "#f7f7f7" }}>
              <tr>
                <th>Employee Name</th>
                <th>Date</th>
                <th>Check-In Time</th>
                <th>Check-Out Time</th>
                <th>Total Hours</th>
                <th>Status</th>
                <th>Remarks / Approved Corrections</th>
                <th>Images</th> {/* New Images Column Header */}
                <th>Notes</th>
                <th>Late</th>
                <th>Late Minutes</th>
                <th>Total / Allowed Hours</th>
                <th>Actions</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={14}>
                    <Center>Loading...</Center>
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={14}>
                    <Center>
                      <div>
                        <Text color="red">Error loading records</Text>
                        {errorMessage && <Text size="xs">{errorMessage}</Text>}
                      </div>
                    </Center>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={14}>
                    <Center>No records found</Center>
                  </td>
                </tr>
              ) : (
                rows
              )}
            </tbody>
          </Table>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <Text className={classes.smallMuted}>
            Showing{" "}
            {records.length === 0
              ? 0
              : (pagination.currentPage - 1) * pagination.recordsPerPage +
                1}{" "}
            -{" "}
            {Math.min(
              pagination.currentPage * pagination.recordsPerPage,
              pagination.totalRecords
            )}{" "}
            of {pagination.totalRecords} entries
          </Text>

          <Group>
            <Select
              data={["5", "10", "25"].map((s) => ({ value: s, label: s }))}
              value={String(rowsPerPage)}
              onChange={handleRowsPerPageChange}
              style={{ width: 100 }}
            />

            <Pagination
              total={pagination.totalPages || 1}
              page={pagination.currentPage || page}
              onChange={(p) => setPage(p)}
              siblings={1}
            />
          </Group>
        </div>
      </Card>

      {/* Images Modal */}
      <Modal
        opened={imagesModalOpened}
        onClose={() => setImagesModalOpened(false)}
        title={
          <Text className={classes.modalTitle}>
            Images for {selectedEmployeeName}
          </Text>
        }
        size="xl"
        centered
      >
        {selectedImages.length === 0 ? (
          <Center py="xl">
            <Text color="dimmed">No images available</Text>
          </Center>
        ) : (
          <>
            <Text size="sm" color="dimmed" mb="md">
              Total {selectedImages.length} image
              {selectedImages.length > 1 ? "s" : ""} captured
            </Text>
            <Grid gutter="md">
              {selectedImages.map((imageUrl, index) => (
                <Grid.Col key={index} span={6}>
                  <div className={classes.imageContainer}>
                    <Image
                      src={imageUrl}
                      alt={`Image ${index + 1}`}
                      height={200}
                      fit="cover"
                      withPlaceholder
                    />
                  </div>
                </Grid.Col>
              ))}
            </Grid>
          </>
        )}
      </Modal>
    </>
  );
}
